import { ICellEditorParams } from 'ag-grid-community';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { InputType } from '@miq/catalog';

type OnDoneCallback = (params: any, currentValue: string) => void;

@Component({
  selector: 'miq-ag-text',
  styleUrls: ['ag-popup-text.component.scss'],
  templateUrl: 'ag-popup-text.component.html',
  standalone: false,
})
export class AgPopupTextComponent implements ICellEditorAngularComp, AfterViewInit, OnDestroy {
  public currentValue = '';
  public title = '';
  public inputType = InputType;
  public readonly = false;
  public doneText = 'Done';
  public shouldIndent = false;
  public params: any;
  private onDone: OnDoneCallback;
  private focusWhenCreate = false;
  maxAllowedChars = null;
  #defaultMaxAllowedChars = 99;

  @ViewChild('textValue', { static: false }) private textArea: ElementRef;

  ngAfterViewInit(): void {
    if (this.focusWhenCreate) {
      setTimeout(() => (this.textArea ? this.textArea.nativeElement.focus() : ''));
    }
  }

  agInit(params: any): void {
    this.params = params;
    const paramsObject =
      typeof params?.cellEditorParams === 'function' ? params?.cellEditorParams(params) : params?.cellEditorParams;
    this.currentValue = params.value;
    this.readonly = paramsObject?.isReadOnly(params);
    this.title = paramsObject?.title;
    this.onDone = paramsObject?.onDoneClick;
    this.doneText = this.getDoneText(params);
    this.focusWhenCreate = paramsObject?.focusWhenCreate;
    this.shouldIndent = paramsObject?.shouldIndent;

    // Set a big default in case is not configured
    this.maxAllowedChars = params.colDef.maxLength ?? this.#defaultMaxAllowedChars;
  }

  ngOnDestroy() {
    this.textArea = null;
  }

  onInputChange(event: string) {
    this.currentValue = event?.trim();
    this.params?.node?.setDataValue?.(this.params.colDef.field, this.currentValue);
  }

  handleKeydown(event: KeyboardEvent) {
    const target = event.target as HTMLTextAreaElement;
    if (event.key === 'Tab' && this.shouldIndent) {
      event.preventDefault();
      const start = target.selectionStart;
      const end = target.selectionEnd;
      // add the tab indent
      const updatedValue = `${target.value.substring(0, start)}\t${target.value.substring(end)}`;
      target.value = updatedValue;
      target.selectionStart = target.selectionEnd = start + 1;
      // update the node value
      this.params.node.setDataValue(this.params.colDef.field, updatedValue);
    }
  }

  getValue() {
    if (this.shouldIndent) {
      // tab delimiter is not being saved correctly so it is needed to convert to its space value
      this.currentValue = this.currentValue ? this.currentValue.replace(/\t/g, '      ') : '';
    }
    return this.currentValue;
  }

  isPopup(): boolean {
    return true;
  }

  getPopupPosition(): 'over' | 'under' {
    return 'under';
  }

  close() {
    if (this.onDone) {
      this.onDone(this.params, this.currentValue);
    }
    this.params.stopEditing();
  }

  onBlur() {
    if (this.currentValue) {
      this.params.node.setDataValue(this.params.colDef.field, this.currentValue);
    }
  }

  private getDoneText(params: ICellEditorParams): string {
    const parameter = params['doneText'] ? params['doneText'] : 'Done';
    if (typeof parameter === 'function') {
      return parameter(params);
    }
    return parameter;
  }
}
